/**
 * 기능 ID: COMVRF004-10TB
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { deviceUtils, useGlobalErrorStore, useRouter, useThrottle } from 'scbk-services';
import { Dialog, filterBuilder, rulesBuilder, usePageForm, useSession } from 'scbk-shared-module';
import { useCode } from 'scbk-shared-module/modules/Code';
import {
  SecureKeypadFieldOption,
  SecureKeypadResult,
  useSecureKeypad,
} from 'scbk-shared-module/modules/SecureKeypad';
import {
  TermsComponent,
  TermsParameter,
  TermsType,
  WrapConsentListProps,
  WrapSubCheckboxProps,
  useTerms,
} from 'scbk-shared-module/modules/Terms';
import {
  BottomButtons,
  ButtonWithIcon,
  Checkbox,
  ConsentRow,
  ConsentRowWrapper,
  ConsentTop,
  ConsentWrapper,
  FieldRound,
  FieldRoundIcon,
  FieldWrapper,
  NativeSelect,
  NativeSelectOption,
  PhoneField,
} from 'scbk-ui-web';
import { useAutVrfAddApplySMSIdentify } from '../hooks/api/autVrfAdditionalHooks';
import { useTimer } from '../hooks/useTimer';
import { AutVrfAddApplySMSIdentifyRequest } from '../types/api/autVrfAddApplySMSIdentify';
import { TermsUtils } from '../utils/AdditionalUtils';

// 휴대폰 본인인증 유효성 검사 Props
interface IdentifyValidProps {
  terms: {
    agree1: boolean;
    agree2: boolean;
    agree3: boolean;
    agree4: boolean;
    agree5: boolean;
    agree5_1_1: boolean;
    agree5_1_2: boolean;
  };
  custName?: string; // 고객명
  perBusNo1?: string; //주민번호 앞자리
  perBusNo2?: string; //주민번호 뒷자리
  telecom: string; //통신사
  phoneNo1: string;
  phoneNo2: string;
  phoneNo3: string;
}

interface SmsConfirmProps {
  authNumber: string;
}

interface Props {
  onComplete?: () => void;
  isLoading?: boolean;
}

const AdditionalIdentifyComponent: React.FC<Props> = ({ onComplete, isLoading = false }) => {
  const [termsAllChecked, setTermsAllChecked] = useState(false);
  const [isAuthReq, setIsAuthReq] = useState(false);
  const [timer, setTimer] = useState('[03:00]');
  const [isTermsView, setIsTermsView] = useState(false);

  // 다국어 처리
  const { t } = useTranslation();

  const { isLogin } = useSession();
  const router = useRouter();
  const terms = useTerms();

  const { selectCodes } = useCode(['CELLULARPRE', 'TELECOM']);

  const { submit, isError, getFieldError, useFields, setValue, getValues, setFocus } =
    usePageForm<IdentifyValidProps>({
      mode: 'onSubmit',
      defaultValues: {
        terms: {
          agree1: false,
          agree2: false,
          agree3: false,
          agree4: false,
          agree5: false,
          agree5_1_1: false,
          agree5_1_2: false,
        },
        custName: '', // 고객명
        perBusNo1: '',
        perBusNo2: '',
        telecom: '',
        phoneNo1: '010',
        phoneNo2: '',
        phoneNo3: '',
      },
      onSubmitError: (error) => {
        setCompleteValue('authNumber', '');
        setIsAuthReq(false);
        console.log('# onSubmitError ', error);
      },
    });

  const {
    submit: completeSubmit,
    isError: isCompleteError,
    getFieldError: getCompleteFieldError,
    useFields: useCompleteFields,
    setValue: setCompleteValue,
    getValues: getCompleteValues,
    setFocus: setCompleteFocus,
  } = usePageForm<SmsConfirmProps>({
    mode: 'onSubmit',
    defaultValues: {
      authNumber: '',
    },
    onSubmitError: (error) => {
      console.log('# onSubmitError ', error);
    },
  });

  const authNumberFields = useCompleteFields({
    authNumber: {
      filters: filterBuilder().number().build(),
      rules: rulesBuilder<SmsConfirmProps>()
        .required(t('AdditionalComponent.115', '인증번호를 입력해 주세요.'))
        .minLength(6, t('AdditionalComponent.116', '인증번호 6자리를 입력해 주세요.'))
        .build(),
    },
  });

  const fields = useFields({
    'terms.agree1': {
      rules: rulesBuilder<IdentifyValidProps>()
        .validate((v) => {
          return v === 'true' ? true : t('AdditionalComponent.065', '약관에 동의해 주세요.');
        })
        .build(),
    },
    'terms.agree2': {
      rules: rulesBuilder<IdentifyValidProps>()
        .validate((v) => {
          return v === 'true' ? true : t('AdditionalComponent.065', '약관에 동의해 주세요.');
        })
        .build(),
    },
    'terms.agree3': {
      rules: rulesBuilder<IdentifyValidProps>()
        .validate((v) => {
          return v === 'true' ? true : t('AdditionalComponent.065', '약관에 동의해 주세요.');
        })
        .build(),
    },
    'terms.agree4': {
      rules: rulesBuilder<IdentifyValidProps>()
        .validate((v) => {
          return v === 'true' ? true : t('AdditionalComponent.065', '약관에 동의해 주세요.');
        })
        .build(),
    },
    'terms.agree5': {
      rules: rulesBuilder<IdentifyValidProps>()
        .validate((v) => {
          return v === 'true' ? true : t('AdditionalComponent.065', '약관에 동의해 주세요.');
        })
        .build(),
    },
    ...(!isLogin() && {
      custName: {
        filters: filterBuilder().build(),
        rules: rulesBuilder<IdentifyValidProps>()
          .required(t('AdditionalComponent.066', '이름을 입력해 주세요.'))
          .build(),
      },
      perBusNo1: {
        filters: filterBuilder().number().build(),
        rules: rulesBuilder<IdentifyValidProps>()
          .number()
          .required(t('AdditionalComponent.068', '주민등록번호 앞자리를 입력해 주세요.'))
          .minLength(6, t('AdditionalComponent.069', '주민등록번호 앞 6자리를 입력해 주세요.'))
          .build(),
      },
      perBusNo2: {
        rules: rulesBuilder<IdentifyValidProps>()
          .validate((v, ctx) => {
            const el = document.getElementById(ctx.name) as HTMLInputElement;
            const value = v !== '' ? v : (el?.value ?? '');
            if (value.length === 0) {
              return t('AdditionalComponent.070', '주민등록번호 뒷자리를 입력해 주세요.');
            }
            return value.length === 7
              ? true
              : t('AdditionalComponent.071', '주민등록번호 뒤 7자리를 입력해 주세요.');
          })
          .build(),
      },
    }),
    telecom: {
      rules: rulesBuilder<IdentifyValidProps>()
        .required(t('AdditionalComponent.072', '통신사를 선택해 주세요.'))
        .build(),
    },
    phoneNo1: {
      filters: filterBuilder().number().build(),
      rules: rulesBuilder<IdentifyValidProps>()
        .required(t('AdditionalComponent.073', '휴대폰번호를 입력해 주세요.'))
        .build(),
    },
    phoneNo2: {
      filters: filterBuilder().number().build(),
      rules: rulesBuilder<IdentifyValidProps>()
        .required(t('AdditionalComponent.073', '휴대폰번호를 입력해 주세요.'))
        .minLength(4, t('AdditionalComponent.109', '휴대폰번호는 최소 8자리로 입력해 주세요.'))
        .build(),
    },
    phoneNo3: {
      filters: filterBuilder().number().build(),
      rules: rulesBuilder<IdentifyValidProps>()
        .required(t('AdditionalComponent.073', '휴대폰번호를 입력해 주세요.'))
        .minLength(4, t('AdditionalComponent.109', '휴대폰번호는 최소 8자리로 입력해 주세요.'))
        .build(),
    },
  });

  const identifyAgreeList: WrapConsentListProps[] = [
    {
      id: 'terms.agree1',
      name: 'terms.agree1',
      title: t('AdditionalComponent.056', '[필수] 이동통신사별 서비스 이용약관'),
      hasCheckbox: true,
      hasConfirm: false,
      active: false,
      prvsnCd: 'PRVCMM000287',
      termsType: TermsType.HTML_EXPLAIN,
    },
    {
      id: 'terms.agree2',
      name: 'terms.agree2',
      title: t('AdditionalComponent.057', '[필수] 서비스 이용약관 동의'),
      hasCheckbox: true,
      active: false,
      prvsnCd: 'PRVCMM000289',
      termsType: TermsType.HTML_EXPLAIN,
    },
    {
      id: 'terms.agree3',
      name: 'terms.agree3',
      title: t('AdditionalComponent.080', '[필수] 개인정보 수집, 이용 및 취급위탁동의'),
      hasCheckbox: true,
      active: false,
      prvsnCd: 'PRVCMM000284',
      termsType: TermsType.HTML_EXPLAIN,
    },
    {
      id: 'terms.agree4',
      name: 'terms.agree4',
      title: t('AdditionalComponent.081', '[필수] 고유식별정보처리 동의'),
      hasCheckbox: true,
      active: false,
      prvsnCd: 'PRVCMM000285',
      termsType: TermsType.HTML_EXPLAIN,
    },
    {
      id: 'terms.agree5',
      name: 'terms.agree5',
      title: t('AdditionalComponent.060', '[필수] 개인(신용)정보 수집•이용 동의서'),
      subTitle: t('AdditionalComponent.061', '[비여신금융거래용]'),
      hasCheckbox: true,
      active: false,
      groupId: 'agree5',
      depth: 1,
      prvsnCd: 'PRVCMM000202',
      termsType: TermsType.HTML_EXPLAIN,
    },
    {
      id: 'terms.agree5_1',
      name: 'terms.agree5_1',
      title: t('AdditionalComponent.062', '수집·이용에 관한 사항'),
      hasCheckbox: false,
      active: false,
      groupId: 'agree5',
      depth: 2,
      agreeNumber: 1,
      prvsnCd: 'PRVCMM000202',
      termsType: TermsType.HTML_EXPLAIN,
      subCheckbox: [
        {
          id: 'terms.agree5_1_1',
          name: 'terms.agree5_1_1',
          title: t('AdditionalComponent.063', '고유식별정보 수집·이용 동의'),
          groupId: 'agree5',
          depth: 3,
          agreeNumber: 1,
          prvsnCd: 'PRVCMM000202',
          termsType: TermsType.HTML_EXPLAIN,
        },
        {
          id: 'terms.agree5_1_2',
          name: 'terms.agree5_1_2',
          title: t('AdditionalComponent.064', '개인(신용)정보 수집·이용 동의'),
          groupId: 'agree5',
          depth: 3,
          agreeNumber: 1,
          prvsnCd: 'PRVCMM000202',
          termsType: TermsType.HTML_EXPLAIN,
        },
      ],
    },
  ];

  // 약관 (S)
  const utils = useMemo(
    () => TermsUtils<typeof fields>(setValue, getValues, identifyAgreeList, setTermsAllChecked),
    [setValue, getValues, identifyAgreeList, termsAllChecked, setTermsAllChecked],
  );

  const openTerms = ({
    index,
    subIndex,
    active,
  }: {
    index: number;
    subIndex?: number;
    active?: boolean;
  }) => {
    const info: WrapSubCheckboxProps | WrapConsentListProps | undefined =
      subIndex !== undefined
        ? identifyAgreeList[index]?.subCheckbox?.[subIndex]
        : identifyAgreeList[index];

    if (!info) return;

    if (!active) {
      terms.openTerms({
        ...(info as TermsParameter),
        onClose: () => {
          utils.setChecked({ index, subIndex, active: true });
        },
      });
    } else {
      utils.setChecked({ index, subIndex, active: false });
    }
  };

  const isTermCurrentIdx = useRef(0);
  const openTermsAll = useCallback(async () => {
    const filteredList = identifyAgreeList.filter((item) => item.hasCheckbox);
    isTermCurrentIdx.current = 0;

    if (!termsAllChecked) {
      setIsTermsView(true);

      //   terms.openTerms({
      //     ...identifyAgreeList[4],
      //     onClose: () => {
      //       utils.setAllChecked(true);
      //     },
      //   });

      const openNext = () => {
        const current = filteredList[isTermCurrentIdx.current];
        if (!current) {
          setTimeout(() => {
            setIsTermsView(false);
          }, 2000);

          if (isLogin()) {
            requestAnimationFrame(() => {
              setFocus('telecom');
            });
          } else {
            requestAnimationFrame(() => {
              setFocus('custName');
            });
          }
          return;
        }

        if (getValues(current.id as keyof typeof fields) === 'true') {
          isTermCurrentIdx.current++;
          openNext();
          return;
        }

        current.triggerCloseEvent = true;

        terms.openTerms({
          ...current,
          onClose: (result) => {
            if (result) {
              utils.setChecked({ index: isTermCurrentIdx.current, active: true });
              isTermCurrentIdx.current++;
              openNext();
            } else {
              setTimeout(() => {
                setIsTermsView(false);
              }, 2000);
            }
          },
        });
      };
      openNext();
    } else {
      utils.setAllChecked(false);
    }
  }, [identifyAgreeList, termsAllChecked]);
  // 약관 (E)

  const { requestWithSecure: applySMSIdentify } = useAutVrfAddApplySMSIdentify({
    onError: (error) => {
      // 키패드값 초기화
      if (!isLogin()) {
        fields.perBusNo2.onChangeValue('');
        secureKeypad.clear();
      }

      if (error.code === 'B131') {
        error.errorMessage = t('AdditionalComponent.117', '인증번호를 다시 입력해 주세요.');
      }
      useGlobalErrorStore.getState().setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const sendAuthNumber = useCallback(async () => {
    const request: AutVrfAddApplySMSIdentifyRequest = {
      mode: 'PASS',
      pageStep: 'STEP1',
      telecom: getValues('telecom'),
      telNo: getValues('phoneNo1') + getValues('phoneNo2') + getValues('phoneNo3'),
      deptCode: 'GL9-KI3-HS9',
      infoTelNo: '1588-1599',
      mertName: 'SC제일은행',
      indentifyCheckYn: 'N',
      custName: getValues('custName') ?? '',
      perBusNo1: getValues('perBusNo1') ?? '',
    };

    const result = await applySMSIdentify(request);

    if (result?.minorYn === 'Y') {
      const isOk = await Dialog.alert({
        content: t('AdditionalComponent.092', '미성년자(만19세 미만)는 서비스 이용이 불가합니다.'),
      });

      if (isOk) {
        router.firstBack();
      }

      return;
    }

    if (result?.foreignerYn === 'Y') {
      const isOk = await Dialog.alert({
        content: t('AdditionalComponent.093', '외국인 및 재외국민은 서비스를 이용하실 수 없어요.'),
      });

      if (isOk) {
        router.firstBack();
      }

      return;
    }

    setIsAuthReq(true);

    const bohosvcyn = result?.bohoSvcYn || '';

    if (bohosvcyn != 'P') {
      // SMS번호인증...
      const isOk = await Dialog.alert({
        content: t('AdditionalComponent.094', '인증번호를 전송하였습니다.'),
      });

      //keypad 재설정 (초기화방지)
      secureKeypad.setKeypadEncData();

      setTimer('[03:00]');
      restart();

      if (isOk) {
        setCompleteValue('authNumber', '');
        setCompleteFocus('authNumber');
      }
    } else {
      // PASS 인증.... 흠....
    }
  }, [getValues]);

  const confirmSMSIdentify = useCallback(
    async ({ onComplete }: { onComplete?: () => void }) => {
      if (!getCompleteValues('authNumber')) {
        Dialog.alert({ content: t('AdditionalComponent.095', '인증번호를 입력해 주세요.') });
        return;
      }

      const request: AutVrfAddApplySMSIdentifyRequest = {
        pageStep: 'STEP2',
        telecom: getValues('telecom'),
        telNo: getValues('phoneNo1') + getValues('phoneNo2') + getValues('phoneNo3'),
        deptCode: 'GL9-KI3-HS9',
        infoTelNo: '1588-1599',
        mertName: 'SC제일은행',
        indentifyCheckYn: 'N',
        custName: getValues('custName'),
        perBusNo1: getValues('perBusNo1'),
        authNumber: getCompleteValues('authNumber'),
      };

      const result = await applySMSIdentify(request);

      if (result?.nameErrYn === 'Y') {
        await Dialog.alert({
          content: t(
            'AdditionalComponent.096',
            '입력한 고객정보(성명)를 다시 확인해 주세요. [KCB003]',
          ),
          onClick: () => {
            router.back({ backToEntry: true });
          },
        });
        return;
      }

      if (result?.ciErrYn === 'Y') {
        await Dialog.alert({
          content: [
            t('AdditionalComponent.097', '고객정보가 일치 하지 않습니다.(실명번호 불일치)'),
            t('AdditionalComponent.098', 'SC제일은행 고객컨택센터(1588-1599)로 연락 부탁드립니다.'),
          ],
          onClick: () => {
            router.back({ backToEntry: true });
          },
        });
        return;
      }

      if (result?.kcbCiErrYn === 'Y') {
        await Dialog.alert({
          content: t('AdditionalComponent.099', '입력한 고객정보를 다시 확인해 주세요. [KCB001]'),
          onClick: () => {
            router.back({ backToEntry: true });
          },
        });
        return;
      }

      onComplete?.();
    },
    [getValues, getCompleteValues],
  );

  const { restart } = useTimer({
    duration: 180,
    interval: 1,
    onTick: (tick, duration) => {
      const currentTime = duration - tick;
      const min = Math.floor(currentTime / 60);
      const sec = currentTime % 60;
      setTimer(`[${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}]`);
    },
    onComplete: () => {
      setTimer(t('AdditionalComponent.100', '만료'));
      console.log('useTimer onComplete');
    },
  });

  /**
   * 키패드 정보 배열
   */
  const secureKeypadList: SecureKeypadFieldOption[] = [
    {
      fieldId: 'perBusNo2', // input id
      // aesEnc: '0',
      onComplete: (result: SecureKeypadResult) => {
        // 가상키패드 입력 완료 시 호출 함수
        console.log('### normal result : ', result);
        requestAnimationFrame(() => {
          setFocus('telecom');
        });
      },
    },
  ];

  /**
   * useSecureKeypad hooks 선언
   */
  const secureKeypad = useSecureKeypad(secureKeypadList);

  const handleSendAuthNumber = submit(async () => {
    sendAuthNumber();
  });

  const handleConfirmSMSIdentify = submit(async () => {
    completeSubmit(async () => {
      confirmSMSIdentify({ onComplete });
    })();
  });

  const throttleClick = useThrottle(
    submit(async () => {
      if (!isAuthReq) {
        handleSendAuthNumber();
      } else {
        handleConfirmSMSIdentify();
      }
    }),
    1000,
  );

  return (
    <>
      <h2 className="page-heading-title mb-8">
        <Trans
          t={t}
          i18nKey="AdditionalComponent.006"
          components={[<span className="block md:inline md:mr-1"></span>]}
        >
          <span className="block md:inline md:mr-1">본인 명의 휴대폰으로</span>
          인증해 주세요
        </Trans>
      </h2>
      <ConsentWrapper>
        <ConsentTop>
          <Checkbox
            label={t('AdditionalComponent.007', '전체동의')}
            id="check-all"
            value="check-all"
            checked={termsAllChecked}
            onClick={() => openTermsAll()}
          />
        </ConsentTop>
        <ConsentRowWrapper>
          {identifyAgreeList.map((item, idx) => (
            <ConsentRow
              key={`agree-list2-${idx}`}
              hasCheckbox={item.hasCheckbox}
              action={item.hasConfirm ? 'confirm' : 'arrow'}
              onClick={() => {
                openTerms({
                  index: idx,
                  active: false,
                });
              }}
              subCheckbox={item?.subCheckbox?.map((sub, subIdx) => (
                <FieldWrapper key={`agree-list2-${idx}-${subIdx}`} isIndented>
                  <Checkbox
                    key={sub.id}
                    variants="icon"
                    label={sub.title}
                    id={sub.id}
                    weight="light"
                    checked={getValues(sub.id as keyof typeof fields) === 'true'}
                    onClick={() => {
                      openTerms({
                        index: idx,
                        subIndex: subIdx,
                        active: getValues(sub.id as keyof typeof fields) === 'true',
                      });
                    }}
                  />
                </FieldWrapper>
              ))}
            >
              {item.hasCheckbox ? (
                <FieldWrapper
                  isIndented
                  labelFor={item.id}
                  isError={isError(item.id as keyof typeof fields)}
                  helpMessage={getFieldError(item.id as keyof typeof fields)}
                >
                  <Checkbox
                    label={item.title}
                    desc={item.subTitle}
                    id={item.id}
                    weight="light"
                    checked={getValues(item.id as keyof typeof fields) === 'true'}
                    onClick={() => {
                      openTerms({
                        index: idx,
                        active: getValues(item.id as keyof typeof fields) === 'true',
                      });
                    }}
                    ref={fields[item.id as keyof typeof fields].ref}
                  />
                </FieldWrapper>
              ) : (
                item.title
              )}
            </ConsentRow>
          ))}
        </ConsentRowWrapper>
      </ConsentWrapper>

      <div className="flex flex-col gap-6 mt-8">
        {!isLogin() && (
          <>
            <FieldWrapper
              labelFor="custName"
              title={t(`AdditionalComponent.008`, `성명`)}
              isError={isError('custName')}
              helpMessage={getFieldError('custName')}
            >
              <FieldRound
                {...fields.custName.inputProps}
                id="custName"
                placeholder={t(`AdditionalComponent.009`, `실명(한글)입력`)}
                maxLength={15}
                onChange={(e) => {
                  fields.custName.inputProps?.onChange(e);
                }}
                error={isError('custName')}
              />
            </FieldWrapper>

            <FieldWrapper
              title={t(`AdditionalComponent.010`, `주민등록번호`)}
              isError={isError('perBusNo1') || isError('perBusNo2')}
              helpMessage={getFieldError('perBusNo1') || getFieldError('perBusNo2')}
            >
              <div className="flex items-center gap-2">
                <FieldRound
                  id="perBusNo1"
                  className="w-[50%]"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={t(`AdditionalComponent.011`, `앞 6자리`)}
                  aria-label={t(`AdditionalComponent.012`, `주민등록번호: 앞 6자리`)}
                  {...fields.perBusNo1.inputProps}
                  onChange={(e) => {
                    fields.perBusNo1.inputProps?.onChange(e);
                    if (e.target.value.length == 6) {
                      requestAnimationFrame(() => {
                        setFocus('perBusNo2');
                        secureKeypad.open('perBusNo2');
                      });
                    }
                  }}
                  error={isError('perBusNo1')}
                />

                <FieldRound
                  id="perBusNo2"
                  className="w-[50%]"
                  type="password"
                  data-enc="num" // 가상키패드 입력 타입 'num' : 숫자 키패드 / 'qwerty' : 문자키패드
                  data-aesenc="0"
                  maxLength={7}
                  placeholder={t(`AdditionalComponent.013`, `뒤 7자리`)}
                  aria-label={t(`AdditionalComponent.014`, `주민등록번호: 뒤 6자리`)}
                  ref={(el: HTMLInputElement) => {
                    const originRef = fields.perBusNo2.inputProps.ref;
                    originRef(el);
                    secureKeypad.ref?.(el);
                  }}
                  onFocus={() => secureKeypad.open('perBusNo2')}
                  onChange={(e) => fields.perBusNo2.inputProps.onChange(e)}
                  error={isError('perBusNo2')}
                />
              </div>
            </FieldWrapper>
          </>
        )}

        <FieldWrapper
          labelFor="telecom"
          title={t(`AdditionalComponent.015`, `휴대폰번호`)}
          isError={
            isError('telecom') || isError('phoneNo1') || isError('phoneNo2') || isError('phoneNo3')
          }
          helpMessage={
            getFieldError('telecom') ||
            getFieldError('phoneNo1') ||
            getFieldError('phoneNo2') ||
            getFieldError('phoneNo3')
          }
        >
          <NativeSelect
            id="telecom"
            {...fields.telecom.inputProps}
            value={getValues('telecom')}
            onChange={(e) => {
              fields.telecom.inputProps?.onChange(e);
              setIsAuthReq(false);
              if (!deviceUtils.isIOS()) {
                setFocus('phoneNo2');
              }
            }}
            onBlur={(e) => {
              fields.telecom.inputProps?.onBlur(e);
              if (deviceUtils.isIOS()) {
                setFocus('phoneNo2');
              }
            }}
            isValid={
              isError('telecom') ||
              isError('phoneNo1') ||
              isError('phoneNo2') ||
              isError('phoneNo3')
            }
          >
            {selectCodes('TELECOM') &&
              selectCodes('TELECOM')?.map((item, idx) => {
                return (
                  <NativeSelectOption value={item.value} key={`telecom-${idx}-${item.value}`}>
                    {item.label}
                  </NativeSelectOption>
                );
              })}
          </NativeSelect>

          <PhoneField
            id="phoneNo"
            selectOption={selectCodes('CELLULARPRE')}
            prefixSelect={{
              ...fields.phoneNo1.inputProps,
              value: getValues('phoneNo1'),
              onChange: (e) => {
                fields.phoneNo1.inputProps?.onChange(e);
                setIsAuthReq(false);
                if (!deviceUtils.isIOS()) {
                  setFocus('phoneNo2');
                }
              },
              onBlur: (e) => {
                fields.phoneNo1.inputProps?.onBlur(e);
                if (deviceUtils.isIOS()) {
                  setFocus('phoneNo2');
                }
              },
            }}
            middleInput={{
              ...fields.phoneNo2.inputProps,
              value: getValues('phoneNo2'),
              onChange: (e) => {
                fields.phoneNo2.inputProps?.onChange(e);
                setIsAuthReq(false);
                if (e.target.value.length == 4) {
                  requestAnimationFrame(() => {
                    setFocus('phoneNo3');
                  });
                }
              },
            }}
            lastInput={{
              ...fields.phoneNo3.inputProps,
              value: getValues('phoneNo3'),
              onChange: (e) => {
                fields.phoneNo3.inputProps?.onChange(e);
                setIsAuthReq(false);
                requestAnimationFrame(() => {
                  setValue('phoneNo3', e.target.value);
                });
              },
            }}
            error={
              isError('telecom') ||
              isError('phoneNo1') ||
              isError('phoneNo2') ||
              isError('phoneNo3')
            }
          />
        </FieldWrapper>

        {isAuthReq && (
          <FieldWrapper
            labelFor="authNumber"
            title={t(`AdditionalComponent.017`, `SMS 인증번호 입력`)}
            aria-label={t(`AdditionalComponent.017`, `SMS 인증번호 입력`)}
            subTitle={<span className="body-md font-weight-medium text-primary-04">{timer}</span>}
            className="justify-start"
            isError={isCompleteError('authNumber')}
            helpMessage={getCompleteFieldError('authNumber')}
          >
            <FieldRoundIcon
              {...authNumberFields.authNumber.inputProps}
              id="authNumber"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder={t('AdditionalComponent.018', '숫자 6자리')}
              rightIcon={
                <ButtonWithIcon
                  title={t(`AdditionalComponent.019`, `인증번호 재요청`)}
                  icon="redirect"
                  direction="right"
                  onClick={() => {
                    handleSendAuthNumber();
                  }}
                />
              }
              aria-label={t(`AdditionalComponent.019`, `인증번호 재요청`)}
              className="pr-[140px]"
              onChange={(e) => {
                authNumberFields.authNumber.inputProps?.onChange(e);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                  throttleClick();
                }
              }}
              error={isCompleteError('authNumber')}
            />
          </FieldWrapper>
        )}
      </div>

      <BottomButtons
        buttons={[
          {
            label: isAuthReq
              ? t(`AdditionalComponent.020`, `인증번호 확인`)
              : t(`AdditionalComponent.021`, `인증번호 요청`),
            onClick: () => {
              if (!isLoading && !isTermsView) {
                throttleClick();
              }
            },
            Variants: 'primary',
            disabled: isLoading || isTermsView,
          },
        ]}
      />
      {/* 약관뷰어 공통 컴포넌트 */}
      {terms.termsPopup.isOpen && <TermsComponent {...terms} />}
    </>
  );
};

export default AdditionalIdentifyComponent;
